import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import CryptoJS from "crypto-js";
import { toast } from "sonner";
import { policyView } from "../../../../redux/features/policyDetailsSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { createCaseComplaint } from "../../../../redux/features/crm/caseComplaintSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { getClientSource } from "../../../../utils/getClientSource";
import DomesticHelperReviewCancellationPage from "./domesticHelperReviewCancellationPage";
import DomesticHelperCancellationReasonPage from "./domesticHelperCancellationReasonPage";
import DomesticHelperConfirmCancellationPage from "./domesticHelperConfirmCancellationPage";
import useScreenResolution from "../../../../hooks/useScreenResolution";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { resetPolicyCancellationFlow } from "../../../../redux/features/motorInsurancePolicyCancellationFlowSlice";
import {
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION,
} from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/data";

const CancelPolicyDomesticHelper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { policy_id } = useParams();
  const translate = useLanguageTranslate();
  const screenResolution = useScreenResolution();

  // Use flow hook for step management
  const { currentStep } = usePolicyCancellationFlow();
  const [selectedReason, setSelectedReason] = useState("");

  // Policy decryption
  const secretKey = "i-hate-meej";
  const base64EncryptedValue = (policy_id || "")
    .split("-")
    .join("+")
    .split("_")
    .join("/");
  let originalValue = "";
  try {
    if (base64EncryptedValue) {
      const bytes = CryptoJS.AES.decrypt(base64EncryptedValue, secretKey);
      originalValue = bytes.toString(CryptoJS.enc.Utf8);
    }
  } catch (e) { originalValue = ""; }
  const policyNumber = originalValue;

  // Redux state
  const policyDetails = useSelector((state) => state.policyDetails?.PolicyDetails);
  const policyDetailsStatus = useSelector((state) => state.policyDetails?.status);
  const profileInfo = useSelector(selectProfileData);
  const caseComplaintStatus = useSelector((state) => state.caseComplaint?.status);

  // Load policy details and reset flow
  useEffect(() => {
    dispatch(resetPolicyCancellationFlow());

    if (policyDetails == null || policyDetails?.PolicyHeader?.PolicyNumber !== policyNumber) {
      dispatch(policyView({
        PolicyNumber: policyNumber,
        Channel: getClientSource(),
      }));
    }
  }, [dispatch, policyNumber]);

  // Navigation handlers
  const handleNavigationToPolicyOverview = () => {
    navigate(`/domestic-helper-insurance/policy-overview/${policy_id}`, {
      state: location.state,
    });
  };

  // Handle final confirmation and API call
  const confirmedCancellation = async () => {
    const policyDetailsPartyInfo = policyDetails?.PolicyHeader?.Party;
    
    const payload = {
      FirstName: policyDetailsPartyInfo?.FirstName || profileInfo?.FirstName || "",
      LastName: policyDetailsPartyInfo?.LastName || profileInfo?.LastName || "",
      PhoneNumber: policyDetailsPartyInfo?.PhoneNumber || profileInfo?.PhoneNumber || "",
      Email: profileInfo?.Email || "",
      Subject: "Cancel Policy",
      Product: "Domestic Helper Insurance", // Domestic Helper Insurance
      Lob: "PERLINE", // Domestic Helper Insurance → PERLINE
      Descreption: `Policy to be cancelled. \\nSelected Reason: ${selectedReason}`,
      ClaimNumber: "",
      PolicyNumber: policyNumber,
      VNumber: "", // Empty string for domestic helper insurance (not motor)
      Channel: getClientSource(),
    };

    try {
      const result = await dispatch(createCaseComplaint(payload));
      const unwrappedResult = unwrapResult(result);
      
      toast.success(
        translate(
          "Your cancellation request has been submitted successfully. We will contact you shortly.",
          "تم تقديم طلب الإلغاء بنجاح. سنتواصل معك قريباً."
        )
      );
      handleNavigationToPolicyOverview();
    } catch (error) {
      toast.error(
        translate(
          "There was an error submitting your cancellation request. Please try again.",
          "حدث خطأ في تقديم طلب الإلغاء. يرجى المحاولة مرة أخرى."
        )
      );
    }
  };

  // Render appropriate step component
  const renderPage = () => {
    switch (currentStep?.name) {
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW:
        return (
          <DomesticHelperReviewCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON:
        return (
          <DomesticHelperCancellationReasonPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            selectedReason={selectedReason}
            setSelectedReason={setSelectedReason}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION:
        return (
          <DomesticHelperConfirmCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            confirmedCancellation={confirmedCancellation}
            selectedReasonOut={selectedReason}
          />
        );
      default:
        return (
          <DomesticHelperReviewCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
          />
        );
    }
  };

  return <>{renderPage()}</>;
};

export default CancelPolicyDomesticHelper;